import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBar,
} from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { isObject } from '@principle-theorem/shared';
import { MultiFactorError, signInWithEmailAndPassword } from 'firebase/auth';
import { AuthProcessService, AuthProvider } from '../auth-process.service';
import { NG_AUTH_CONFIG_TOKEN } from '../ngx-auth-firebaseui-old/module-config';
import { AuthService } from '../auth.service';
import { MultifactorService } from './multifactor.service';
import { MULTIFACTOR_VERIFY_ROUTE } from './multifactor.types';
import { isError } from 'lodash';

function isMultiFactorError(error: unknown): error is MultiFactorError {
  return (
    isObject(error) &&
    'code' in error &&
    error.code === 'auth/multi-factor-auth-required'
  );
}

/**
 * Extends the AuthProcessService to intercept email/password sign-in and route
 * it through the modular Firebase SDK. This allows proper handling of
 * MultiFactorError during sign-in.
 *
 * For all other auth providers (Google, Facebook, etc.), delegates to the
 * parent implementation.
 */
@Injectable()
export class MultifactorAwareAuthProcessService extends AuthProcessService {
  private _multifactorService = inject(MultifactorService);
  private _router = inject(Router);

  constructor() {
    super(
      inject(Auth),
      inject(AuthService),
      inject(NG_AUTH_CONFIG_TOKEN),
      inject(MatSnackBar),
      inject(MAT_SNACK_BAR_DEFAULT_OPTIONS)
    );
  }

  /**
   * Signs in with the given provider, intercepting MFA challenges for
   * email/password sign-in only.
   *
   * **Firebase Identity Platform MFA limitation**
   * Firebase only fires `MultiFactorError` (error code
   * `auth/multi-factor-auth-required`) during the email/password credential
   * exchange. OAuth providers (Google, Apple, Microsoft, etc.) complete their
   * own token exchange server-side and are never interrupted by a
   * `MultiFactorError` on the client, regardless of whether the account has
   * enrolled MFA factors.
   *
   * **Non-email provider behaviour**
   * When `provider` is anything other than `AuthProvider.EmailAndPassword`,
   * this method delegates directly to the parent `AuthProcessService`
   * implementation with no MFA interception. The user is signed in
   * immediately, even if their account has enrolled MFA factors.
   *
   * **Compensating control — `MultifactorEnforcementGuard`**
   * The gap above is closed by `MultifactorEnforcementGuard`, which runs on
   * every authenticated route entry (regardless of sign-in method). The guard
   * reads the resolved organisation/user MFA settings and checks whether the
   * current Firebase user has enrolled factors via
   * `MultifactorService.getEnrolledFactors()`. If MFA is required but no
   * factors are enrolled, the guard redirects to the MFA enrollment page
   * (`MULTIFACTOR_ENROLLMENT_ROUTE`). This means:
   *
   * - A Google-authenticated user whose organisation requires MFA will be
   *   redirected to enrol on their first post-login navigation.
   * - A Google-authenticated user who has already enrolled factors is allowed
   *   through immediately (Firebase validates their factors at the OAuth layer).
   * - The check is session-scoped (in-memory flag) so it only runs once per
   *   login session, not on every route change.
   */
  override async signInWith(
    provider: AuthProvider,
    credentials?: { email: string; password: string }
  ): Promise<void> {
    if (provider !== AuthProvider.EmailAndPassword) {
      return super.signInWith(provider, credentials);
    }

    if (!credentials) {
      this.handleError(new Error('Email and password are required.'));
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(
        this._auth,
        credentials.email,
        credentials.password
      );
      this.handleSuccess(userCredential);
    } catch (error) {
      if (isMultiFactorError(error)) {
        const context =
          this._multifactorService.resolveMultiFactorSignIn(error);
        this._authService.setMultifactorVerificationContext(context);
        await this._router.navigate([MULTIFACTOR_VERIFY_ROUTE]);
        return;
      }

      if (isError(error)) {
        this.handleError(error);
      }
    }
  }
}
